// src/pages/ForgotPassword.tsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import { Mail, ArrowLeft, Shield, AlertCircle } from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        toast.success("Password reset OTP sent to your email!");
        // Redirect directly to combined reset page
        navigate("/reset-password", { state: { email } });
      } else {
        toast.error(response.message || "Failed to send OTP");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </div>

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Reset Password
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Enter your email to receive a reset OTP
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className={GlobalStyles.card}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className={GlobalStyles.inputLabel}>
                Email Address
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${GlobalStyles.input} pl-10`}
                  placeholder="you@example.com"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                    ></div>
                    Sending OTP...
                  </div>
                ) : (
                  "Send Reset OTP"
                )}
              </button>
            </div>
          </form>

          {/* Email Verification Notice */}
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  OTP Verification
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                  You'll receive a 6-digit OTP that expires in 10 minutes. Check
                  your spam folder if you don't see it.
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Remember your password?
                </span>
              </div>
            </div>

            {/* Login Link */}
            <div className="mt-6">
              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full`}
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-6`}>
          <Shield className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-sm">
              Your security is our priority. Password reset OTPs are encrypted
              and securely transmitted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
